/**
 * Canal › Saúde da conta (Shopee; o Mercado Livre entra com as docs de qualidade das publicações). Tudo o que pode
 * restringir a loja, num lugar só:
 *   - resumo: nota da loja, pontos de penalidade do trimestre, restrições, anúncios com problema, pedidos atrasados,
 *     conteúdo a melhorar e os alertas abertos
 *   - métricas por grupo (envio, anúncios, atendimento) com o período atual, o anterior e a meta da própria Shopee;
 *     "Ver" abre os pedidos ou anúncios por trás da métrica (na hora, da Shopee)
 *   - pontos de penalidade, restrições, anúncios com problema (violação, sugestão e prazo), pedidos atrasados,
 *     qualidade do conteúdo (o que falta por anúncio) e avisos do Seller Center
 * A leitura é automática de hora em hora (conteúdo: 1x por dia); "Ler agora" lê na hora. Só leitura no canal.
 */
import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, dateTime, int } from '../api.ts';
import { ErrorBox, Loading, Tag } from '../components/ui.tsx';
import { pickAccount, useSelectedAccountId } from '../components/AccountPicker.tsx';
import { useConnectedChannels } from '../components/Shell.tsx';
import { Head } from './MeliChannel.tsx';

interface Metric {
  metric: string; value: number | null; valueText: string | null; threshold: number | null; level: string; collectedAt: string;
  shopeeId?: number; label?: string; group?: string | null; unit?: string; comparator?: string | null; lastPeriod?: number | null; parentId?: number | null; hasDetail?: boolean;
}
interface Item { ref: string; level: string; data: Record<string, any>; firstSeenAt: string; lastSeenAt: string; resolvedAt: string | null; sku: string | null; title: string | null; productId: string | null }
interface Health {
  account: { id: string; channel: string; name: string; contentAt: string | null }; collectedAt: string | null; metrics: Metric[];
  alerts: { metric: string; level: string; message: string; openedAt: string }[];
  penalties: Item[]; punishments: Item[]; listingIssues: Item[]; lateOrders: Item[]; notices: Item[];
  content: { toImprove: number; qualified: number; byTask: { issue: number; label: string; count: number }[]; items: (Item & { tasks: string[] })[] };
}
const TONE: Record<string, string> = { ok: 'ok', attention: 'warn', critical: 'err', unavailable: 'off' };
const LEVEL: Record<string, string> = { ok: 'na meta', attention: 'perto do limite', critical: 'fora da meta', unavailable: 'sem dado no período' };
const num = (v: number | null | undefined, unit = '') => (v == null ? '—' : `${v.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}${unit}`);
const day = (ts: number | null | undefined) => (ts ? new Date(Number(ts) * 1000).toLocaleDateString('pt-BR') : '—');
const GROUPS = ['Envio e cumprimento', 'Anúncios', 'Atendimento'];

export function ChannelHealth({ channel }: { channel: 'shopee' }) {
  const connected = useConnectedChannels();
  const selected = useSelectedAccountId(channel);
  const account = pickAccount(connected.find((c) => c.code === channel)?.accounts, selected);
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['health', account?.id], enabled: !!account, queryFn: () => api<Health>(`/channels/accounts/${account!.id}/health`) });
  const refresh = useMutation({ mutationFn: () => api(`/channels/accounts/${account!.id}/health/refresh`, { method: 'POST', json: {} }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['health'] }); qc.invalidateQueries({ queryKey: ['notifications'] }); } });
  const head = (
    <Head title="Saúde da conta">
      <p>O que pode restringir a loja na Shopee: a nota da loja, cada métrica com a meta da Shopee, pontos de penalidade, restrições, anúncios com problema,
        pedidos atrasados e a qualidade do conteúdo. A Berytus lê de hora em hora (o conteúdo, uma vez por dia) e avisa no sino quando algo sai da meta.</p>
    </Head>
  );
  if (!account) return <>{head}<div className="empty"><b>Nenhuma loja conectada</b>Conecte a loja da Shopee em Integração de canais.</div></>;
  if (q.error) return <>{head}<ErrorBox error={q.error} /></>;
  if (!q.data) return <>{head}<Loading /></>;
  const d = q.data;
  const m = Object.fromEntries(d.metrics.map((x) => [x.metric, x]));
  const shopeeMetrics = d.metrics.filter((x) => x.shopeeId != null);
  const card = (label: string, value: string, level: string | undefined, hint?: string) => (
    <div className="panel" style={{ padding: 14, display: 'grid', gap: 4, borderLeft: `4px solid var(--${level === 'critical' ? 'err' : level === 'attention' ? 'warn' : level === 'ok' ? 'ok' : 'line'})` }}>
      <span className="small muted">{label}</span><b style={{ fontSize: 22 }}>{value}</b>{hint && <span className="small muted">{hint}</span>}
    </div>
  );
  return (
    <>
      {head}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
        <span className="small muted">{d.collectedAt ? `Lida em ${dateTime(d.collectedAt)}` : 'Ainda não lida: a primeira leitura sai em até 1 hora.'}
          {d.account.contentAt ? ` · conteúdo em ${dateTime(d.account.contentAt)}` : ''}</span>
        <button type="button" className="btn sm" disabled={refresh.isPending} onClick={() => refresh.mutate()}>{refresh.isPending ? 'Lendo…' : 'Ler agora'}</button>
        {refresh.error && <ErrorBox error={refresh.error} />}
      </div>
      <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', marginBottom: 'var(--gap)' }}>
        {card('Nota da loja', m.performance?.valueText ?? '—', m.performance?.level)}
        {card('Pontos de penalidade', num(m.penalty_points?.value), m.penalty_points?.level, 'neste trimestre · restrições a partir de 3')}
        {card('Restrições ativas', num(m.punishments?.value), m.punishments?.level)}
        {card('Anúncios com problema', num(d.listingIssues.length), d.listingIssues.length ? 'attention' : 'ok')}
        {card('Pedidos atrasados', num(d.lateOrders.length), d.lateOrders.length ? 'attention' : 'ok')}
        {card('Conteúdo a melhorar', num(d.content.toImprove), d.content.toImprove ? 'attention' : 'ok', `${int(d.content.qualified)} qualificado(s) com sugestões`)}
      </div>
      {d.alerts.length > 0 && (
        <section className="panel stack-y" style={{ gap: 6, marginBottom: 'var(--gap)' }}>
          <h2 style={{ margin: 0 }}>Alertas abertos</h2>
          {d.alerts.map((a) => <div key={a.metric} className="small"><Tag tone={TONE[a.level] ?? 'off'}>{a.level === 'critical' ? 'crítico' : 'atenção'}</Tag> {a.message} <span className="muted">· desde {dateTime(a.openedAt)}</span></div>)}
        </section>
      )}

      <section className="panel flush" style={{ marginBottom: 'var(--gap)' }}>
        <div className="panel-head"><h2>Métricas da Shopee</h2><span className="small muted">período atual × anterior × meta da Shopee</span></div>
        {!shopeeMetrics.length ? <div className="empty"><b>Sem métricas ainda</b>Clique em "Ler agora".</div> : GROUPS.concat('Outras').map((g) => {
          const inGroup = shopeeMetrics.filter((x) => (x.group ?? 'Outras') === g || (g === 'Outras' && x.group && !GROUPS.includes(x.group)));
          // Cada métrica filha (ex.: cancelamento e devolução fazem parte do não cumprimento) logo abaixo da mãe.
          const ids = new Set(inGroup.map((x) => x.shopeeId));
          const list = inGroup.filter((x) => !x.parentId || !ids.has(x.parentId)).flatMap((x) => [x, ...inGroup.filter((c) => c.parentId === x.shopeeId)]);
          if (!list.length) return null;
          return (
            <div key={g}>
              <div className="small" style={{ padding: '10px 16px 4px', fontWeight: 600 }}>{g}</div>
              <table className="simple">
                <thead><tr><th>Métrica</th><th className="num">Atual</th><th className="num">Anterior</th><th className="num">Meta</th><th>Situação</th><th /></tr></thead>
                <tbody>{list.map((x) => <MetricRow key={x.metric} x={x} accountId={d.account.id} />)}</tbody>
              </table>
            </div>
          );
        })}
      </section>

      <ItemsPanel title="Anúncios com problema" empty="Nenhum anúncio com problema." items={d.listingIssues} cols={['Anúncio', 'Problema', 'O que fazer', 'Prazo', 'Desde']}
        row={(i) => [
          <>{i.productId ? <Link to={`/catalogo/${i.productId}`}>{i.title ?? i.data.itemName ?? i.ref}</Link> : i.title ?? i.data.itemName ?? i.ref}<div className="small muted">{i.sku ? `SKU ${i.sku} · ` : ''}item {i.ref}{i.data.deboost ? ' · ranking rebaixado' : ''}</div></>,
          <><Tag tone={TONE[i.level] ?? 'off'}>{i.data.label}</Tag>{(i.data.details ?? []).map((x: any, k: number) => <div key={k} className="small muted">{[x.type, x.reason].filter(Boolean).join(': ')}</div>)}</>,
          <span className="small">{(i.data.details ?? []).map((x: any) => x.suggestion).filter(Boolean).join(' · ') || '—'}
            {(i.data.details ?? []).flatMap((x: any) => x.suggestedCategory ?? []).map((c: any) => <div key={c.category_id} className="muted">Categoria sugerida: {c.category_name}</div>)}</span>,
          day((i.data.details ?? []).map((x: any) => x.deadline).filter(Boolean)[0]),
          dateTime(i.firstSeenAt),
        ]} />

      <ItemsPanel title="Pedidos atrasados" empty="Nenhum pedido atrasado." items={d.lateOrders} cols={['Pedido', 'Prazo de envio', 'Atraso']}
        row={(i) => [<Link to={`/pedidos?q=${encodeURIComponent(i.ref)}&period=all`}>{i.ref}</Link>, day(i.data.deadline), `${i.data.lateByDays} dia(s)`]} />

      <ItemsPanel title="Pontos de penalidade (trimestre)" empty="Nenhum ponto de penalidade neste trimestre." items={d.penalties} cols={['Motivo', 'Pontos', 'Emitido em', 'Situação']}
        row={(i) => [i.data.label, `${i.data.points}${i.data.originalPoints !== i.data.points ? ` (era ${i.data.originalPoints}; recurso aceito)` : ''}`, day(i.data.issuedAt),
          i.resolvedAt ? <Tag tone="off">fora do trimestre</Tag> : <Tag tone="err">vale</Tag>]} />

      <ItemsPanel title="Restrições" empty="Nenhuma restrição ativa." items={d.punishments} cols={['Restrição', 'Início', 'Fim', 'Situação']}
        row={(i) => [<>{i.data.label}{i.data.listingLimit ? <div className="small muted">limite: {i.data.listingLimit} anúncios</div> : null}{i.data.orderLimit ? <div className="small muted">limite de pedidos: {i.data.orderLimit}</div> : null}</>,
          day(i.data.startTime), day(i.data.endTime), i.resolvedAt ? <Tag tone="ok">encerrada</Tag> : <Tag tone="err">em andamento</Tag>]} />

      <section className="panel flush" style={{ marginBottom: 'var(--gap)' }}>
        <div className="panel-head"><h2>Qualidade do conteúdo</h2><span className="small muted">{int(d.content.toImprove)} a melhorar · {int(d.content.qualified)} qualificado(s) com sugestões</span></div>
        {d.content.byTask.length > 0 && <div className="small" style={{ padding: '8px 16px' }}>{d.content.byTask.map((t) => `${t.label}: ${int(t.count)}`).join(' · ')}</div>}
        {!d.content.items.length ? <div className="empty"><b>Nada a melhorar</b>{d.account.contentAt ? 'Nenhum anúncio com conteúdo "a melhorar".' : 'A leitura do conteúdo sai uma vez por dia (ou em "Ler agora").'}</div> : (
          <table className="simple">
            <thead><tr><th>Anúncio</th><th>O que falta</th></tr></thead>
            <tbody>{d.content.items.map((i) => (
              <tr key={i.ref}><td style={{ whiteSpace: 'normal' }}>{i.productId ? <Link to={`/catalogo/${i.productId}`}>{i.title ?? i.ref}</Link> : i.title ?? `item ${i.ref}`}<div className="small muted">{i.sku ? `SKU ${i.sku} · ` : ''}item {i.ref}</div></td>
                <td className="small" style={{ whiteSpace: 'normal' }}>{i.tasks.join(' · ')}</td></tr>))}</tbody>
          </table>
        )}
      </section>

      <ItemsPanel title="Avisos do Seller Center" empty="Nenhum aviso (ou o app não tem acesso aos avisos)." items={d.notices} cols={['Aviso', 'Data']}
        row={(i) => [<>{i.data.url ? <a href={i.data.url} target="_blank" rel="noreferrer">{i.data.title}</a> : <b>{i.data.title}</b>}{i.data.content && <div className="small muted">{i.data.content}</div>}</>, day(i.data.createdAt)]} />
    </>
  );
}

function ItemsPanel({ title, empty, items, cols, row }: { title: string; empty: string; items: Item[]; cols: string[]; row: (i: Item) => React.ReactNode[] }) {
  return (
    <section className="panel flush" style={{ marginBottom: 'var(--gap)' }}>
      <div className="panel-head"><h2>{title}</h2><span className="small muted">{int(items.filter((i) => !i.resolvedAt).length)}</span></div>
      {!items.length ? <div className="empty"><b>Tudo certo</b>{empty}</div> : (
        <table className="simple">
          <thead><tr>{cols.map((c) => <th key={c}>{c}</th>)}</tr></thead>
          <tbody>{items.map((i) => <tr key={i.ref} style={i.resolvedAt ? { opacity: 0.55 } : undefined}>{row(i).map((c, k) => <td key={k} style={{ whiteSpace: 'normal' }}>{c}</td>)}</tr>)}</tbody>
        </table>
      )}
    </section>
  );
}

function MetricRow({ x, accountId }: { x: Metric; accountId: string }) {
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const detail = useQuery({ queryKey: ['health', 'detail', accountId, x.shopeeId, page], enabled: open,
    queryFn: () => api<{ total: number; kind: string | null; rows: Record<string, unknown>[] }>(`/channels/shopee/accounts/${accountId}/health/metrics/${x.shopeeId}?page=${page}`) });
  return (
    <>
      <tr>
        <td style={{ paddingLeft: x.parentId ? 28 : undefined }}>{x.label}</td>
        <td className="num"><b>{num(x.value, x.unit)}</b></td>
        <td className="num muted">{num(x.lastPeriod, x.unit)}</td>
        <td className="num">{x.comparator} {num(x.threshold, x.unit)}</td>
        <td><Tag tone={TONE[x.level] ?? 'off'}>{LEVEL[x.level] ?? x.level}</Tag></td>
        <td>{x.hasDetail && <button type="button" className="btn sm quiet" onClick={() => setOpen(!open)}>{open ? 'Fechar' : 'Ver'}</button>}</td>
      </tr>
      {open && (
        <tr><td colSpan={6} style={{ background: 'var(--hover)' }}>
          {detail.error ? <ErrorBox error={detail.error} /> : !detail.data ? <Loading /> : !detail.data.rows.length ? <span className="small muted">Nada afetado no período.</span> : <DetailTable rows={detail.data.rows} />}
          {detail.data && detail.data.total > 50 && <div className="small" style={{ marginTop: 6 }}>
            {int(detail.data.total)} no total · <button type="button" className="btn sm quiet" disabled={page <= 1} onClick={() => setPage(page - 1)}>Anterior</button>
            <button type="button" className="btn sm quiet" disabled={page * 50 >= detail.data.total} onClick={() => setPage(page + 1)}>Próxima</button></div>}
        </td></tr>
      )}
    </>
  );
}

const COL: Record<string, string> = {
  order_sn: 'Pedido', item_id: 'Item', sku: 'SKU', title: 'Anúncio', shipping_deadline: 'Prazo de envio', actual_shipping_time: 'Enviado em', late_by_days: 'Atraso (dias)',
  actual_pick_up_time: 'Coleta', shipping_channel: 'Transportadora', detailed_reason: 'Motivo', update_time: 'Atualizado', non_fulfillment_type: 'Tipo', cancellation_type: 'Tipo',
  preparation_days: 'Dias de preparo', order_create_time: 'Pago em', handover_deadline: 'Prazo de entrega', handover_time: 'Entregue à transportadora', date: 'Data',
  scheduled_pickup_num: 'Coletas agendadas', failed_pickup_num: 'Coletas falhas', target: 'Meta', first_mile_type: 'Primeira milha',
};
const REASON: Record<number, string> = {
  1: 'Proibido', 2: 'Falsificação', 3: 'Spam', 4: 'Imagem inadequada', 5: 'Informação insuficiente', 6: 'Melhoria (Mall)', 7: 'Outra melhoria', 8: 'Baixa qualidade',
  1001: 'Devolução/reembolso', 1002: 'Cancelamento por divisão do pacote', 1003: 'Falha na coleta', 1004: 'Inclusão do pedido', 10005: 'Sem estoque', 10006: 'Área sem entrega',
  10008: 'Cancelado pela logística', 10009: 'Falha na coleta', 10010: 'Logística não pronta', 10011: 'Vendedor inativo', 10012: 'Vendedor não enviou', 10013: 'Não chegou ao armazém',
  10014: 'Vendedor pediu cancelamento', 10015: 'Não recebido', 10016: 'Item errado', 10017: 'Item danificado', 10018: 'Produto incompleto', 10019: 'Item falso', 10020: 'Defeito', 10021: 'Devolução/reembolso',
};
function DetailTable({ rows }: { rows: Record<string, unknown>[] }) {
  const keys = [...new Set(rows.flatMap((r) => Object.keys(r)))].filter((k) => !Array.isArray(rows[0]?.[k]) && k !== 'order_id' && k !== 'parcel_id');
  const show = (k: string, v: unknown) => {
    if (v == null || v === '') return '—';
    if (k === 'detailed_reason') return REASON[Number(v)] ?? String(v);
    if (k === 'order_sn') return <Link to={`/pedidos?q=${encodeURIComponent(String(v))}&period=all`}>{String(v)}</Link>;
    if (typeof v === 'number' && v > 1_000_000_000 && /time|deadline|date/.test(k)) return day(v);
    return String(v);
  };
  return (
    <table className="simple">
      <thead><tr>{keys.map((k) => <th key={k}>{COL[k] ?? k}</th>)}</tr></thead>
      <tbody>{rows.map((r, i) => <tr key={i}>{keys.map((k) => <td key={k} className="small">{show(k, r[k])}</td>)}</tr>)}</tbody>
    </table>
  );
}
